import { useCallback, useEffect } from "react";
import { AIBubbleButton } from "@/components/assistant-ui/ai-bubble-button";
import { AISidebar } from "@/components/assistant-ui/ai-sidebar";
import { useAppUIStore } from "@/domains/app/stores/app-ui-store";
import { useConfig } from "@/hooks/use-config";
import { useSidebarStore } from "@/lib/store/sidebar";

/**
 * AI Chat Modal Component (Desktop Fixed Sidebar)
 *
 * Provides a fixed right sidebar with a bubble button to toggle visibility
 * Rendered on large screens (>= 1024px / lg breakpoint) including iPad Mini landscape
 * For mobile and iPad Mini portrait, users access AI via mobile sheet
 */
export function AIChatModal() {
  const rightSidebarOpen = useAppUIStore((state) => state.rightSidebarOpen);
  const setRightSidebarOpen = useAppUIStore((state) => state.setRightSidebarOpen);
  const leftSidebarOpen = useSidebarStore((state) => state.open);
  const setLeftSidebarOpen = useSidebarStore((state) => state.setOpen);

  // Check if AI chat is enabled for this salon
  const { value: aiChatEnabled = false } = useConfig("premium_features.ai_chat_enabled");

  // Check if device is iPad (width between 1024px and 1280px)
  const isIPad = useCallback(() => {
    const width = window.innerWidth;
    return width >= 1024 && width < 1280;
  }, []);

  // Auto-collapse left sidebar on iPad when AI sidebar opens
  useEffect(() => {
    if (rightSidebarOpen && isIPad() && leftSidebarOpen) {
      setLeftSidebarOpen(false);
    }
  }, [rightSidebarOpen, leftSidebarOpen, setLeftSidebarOpen, isIPad]);

  // Auto-close right sidebar when screen size is below lg breakpoint (1024px)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024 && rightSidebarOpen) {
        setRightSidebarOpen(false);
      }
    };

    // Check on mount
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [rightSidebarOpen, setRightSidebarOpen]);

  if (!aiChatEnabled) return null;

  return (
    <>
      {/* Floating bubble button - show on lg screens and up (includes iPad Mini landscape) */}
      {!rightSidebarOpen && (
        <div className="hidden lg:block">
          <AIBubbleButton onClick={() => setRightSidebarOpen(true)} />
        </div>
      )}

      {/* Fixed right sidebar - takes up space when open, on lg screens and up */}
      {rightSidebarOpen && (
        <div
          className="hidden lg:block w-[440px] h-full flex-shrink-0"
          // Same treatment as the left sidebar: clear the fixed paused-bookings
          // banner so the AI sidebar header isn't hidden behind it.
          style={{ paddingTop: "var(--paused-banner-height, 0px)" }}
        >
          <AISidebar isOpen={rightSidebarOpen} onClose={() => setRightSidebarOpen(false)} />
        </div>
      )}
    </>
  );
}
